<template>
  <div v-if="!displayTime">
    <button
        id="zButton"
        :disabled="!isConfirmed || isClockOutDisabled || saving"
        class="button-27"
        @click="handleTimer"
    >
      <font-awesome-icon :icon="['fas', iconName]" class="button-icon" />
      {{ title }}
    </button>
  </div>
  <div v-else style="display: flex; align-items: center; justify-content: space-between;">
    <div style="display: flex; align-items: center;">
      <button
          v-if="showClearButton"
          :disabled="!isConfirmed || isClockOutDisabled"
          class="button-44"
          @click="clearTime"
      >
        <font-awesome-icon :icon="['fas', 'times']" />
      </button>
      <div style="display: flex; margin-left: 7px;">
        {{ displayTime }}
      </div>
    </div>
    <button
        v-if="displayTime && !isTiming"
        class="edit-button"
        @click="editTime"
    >
      <font-awesome-icon :icon="['fas', 'pen']" />
    </button>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
  props: ['params'],
  components: {
    FontAwesomeIcon
  },
  data() {
    return {
      title: '',
      displayTime: '',
      colField: '',
      timeOutValue: '',
      timeIn: '',
      isConfirmed: this.params.data?.confirmed,
      saving: false,
      iconName: this.params?.cellRendererParams?.icon || 'clock'
    };
  },
  mounted() {
    this.initializeComponent();
  },
  computed: {
    showClearButton() {
      return !(this.colField === 'timeIn' && this.timeOutValue);
    },
    isClockOutDisabled() {
      return this.colField === 'timeOut' && !this.timeIn;
    },
    // If colField is 'timeIn' and we haven't clocked out (timeOut is empty),
    // we consider it "timing up". So no manual edits allowed during that time.
    isTiming() {
      return this.colField === 'timeIn' && !this.timeOutValue;
    }
  },
  methods: {
    initializeComponent() {
      this.updateLocalData(this.params);
    },
    setTitleBasedOnColumn(columnName) {
      this.title = columnName === 'timeIn' ? 'Clock In' : 'Clock Out';
    },
    updateLocalData(params) {
      this.colField = params.colDef.field;
      this.setTitleBasedOnColumn(this.colField);
      this.updateDisplayTime(params.value);
      if(params.data) {
        this.timeOutValue = params.data['timeOut'] || '';
        this.timeIn = params.data['timeIn'] || '';
        this.isConfirmed = params.data['confirmed'];
      }
      this.saving = false;
    },
    convertUTCtoLocal(utcTime) {
      const utcDate = new Date(utcTime);
      if (isNaN(utcDate.getTime())) throw new Error('Invalid Date');
      return utcDate.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      });
    },
    updateDisplayTime(value) {
      if (value) {
        this.displayTime = this.convertUTCtoLocal(value);
      } else {
        this.displayTime = '';
      }
    },
    clearTime() {
      this.params.node.setDataValue(this.colField, null);
      this.displayTime = '';
      if (this.colField === 'timeOut') {
        this.timeOutValue = '';
      } else if (this.colField === 'timeIn') {
        this.timeIn = '';
      }
    },
    async handleTimer() {
      try {
        this.saving = true;
        const currentDate = new Date();
        const utcTime = currentDate.toISOString();
        this.params.node.setDataValue(this.colField, utcTime);
        this.displayTime = this.convertUTCtoLocal(utcTime);
        if (this.colField === 'timeOut') {
          this.timeOutValue = utcTime;
        } else if (this.colField === 'timeIn') {
          this.timeIn = utcTime;
        }
      } catch (error) {
        console.error('Error setting time:', error);
      } finally {
        this.saving = false;
      }
    },

    // Pencil button click -> manually edit the time
    editTime() {
      try {
        const oldUtc = this.params.data[this.colField];
        if (!oldUtc) return; // No time set yet, just in case.

        // Prompt the user for a new local time, e.g. "10:03:45 PM"
        const userInput = window.prompt(
            'Enter the new time (e.g., "10:03:45 PM"):',
            this.displayTime
        );
        if (!userInput) return; // User canceled.

        // Get the stored UTC date and build a local date/time string to parse
        const oldDate = new Date(oldUtc);       // The old UTC date
        const dateStr = oldDate.toLocaleDateString(); // Keep same local date

        // Attempt to parse the new time in the context of the original date
        const newLocalDateTime = new Date(`${dateStr} ${userInput}`);
        if (isNaN(newLocalDateTime.getTime())) {
          alert('Invalid time format. Please try again.');
          return;
        }

        // Convert local date/time back to UTC for storage
        const newUtcTime = newLocalDateTime.toISOString();

        this.params.node.setDataValue(this.colField, newUtcTime);
        this.updateDisplayTime(newUtcTime);
        if (this.colField === 'timeOut') {
          this.timeOutValue = newUtcTime;
        } else if (this.colField === 'timeIn') {
          this.timeIn = newUtcTime;
        }
      } catch (error) {
        console.error('Error editing time:', error);
      }
    },

    refresh(params) {
      this.updateLocalData(params);
      return true;
    }
  }
};
</script>

<style>
.button-27 {
  appearance: none;
  background-color: #087AD1;
  border: none;
  border-radius: 3px;
  box-sizing: border-box;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: Roobert, -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: normal;
  margin: 0;
  min-width: 0;
  outline: none;
  text-align: center;
  text-decoration: none;
  transition: all 200ms ease-in-out;
  user-select: none;
  -webkit-user-select: none;
  touch-action: manipulation;
  width: 100%;
  padding: 0 4px;
  height: 17px;
}

.button-27:disabled {
  pointer-events: none;
  opacity: 0.5;
  background-color: #ccc;
}

.button-27:hover:not(:disabled) {
  background-color: #066bb3;
  transform: translateY(-1px);
}

.button-27:active:not(:disabled) {
  transform: translateY(0);
}

.button-44 {
  background: #e62143;
  border: none;
  border-radius: 3px;
  box-sizing: border-box;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 500;
  height: 17px;
  width: 17px;
  padding: 0;
  margin: 0 3px;
  transition: all 200ms ease-in-out;
  user-select: none;
  -webkit-user-select: none;
  touch-action: manipulation;
}

.button-44:disabled {
  pointer-events: none;
  opacity: 0.5;
  background-color: #ccc;
}

.button-44:hover:not(:disabled) {
  background-color: #d41b3a;
  transform: translateY(-1px);
}

.button-44:active:not(:disabled) {
  transform: translateY(0);
}

.edit-button {
  background: #5A5A5A;
  border: none;
  border-radius: 3px;
  box-sizing: border-box;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 500;
  height: 17px;
  width: 17px;
  padding: 0;
  margin-left: auto;
  transition: all 200ms ease-in-out;
  user-select: none;
  -webkit-user-select: none;
  touch-action: manipulation;
}

.edit-button:disabled {
  pointer-events: none;
  opacity: 0.5;
  background-color: #ccc;
}

.edit-button:hover:not(:disabled) {
  background-color: #454545;
  transform: translateY(-1px);
}

.edit-button:active:not(:disabled) {
  transform: translateY(0);
}

.button-icon {
  margin-right: 3px;
  font-size: 0.85em;
}
</style>
